import _ from "lodash";
import * as DateUtils from "../../../../../../utils/date/date";
import { BrainTools } from "../../BrainTools/BrainTools";
import { CalcOperation } from "../../Operators/CalcOperations";
import { MathOperations } from "../../Operators/MathOperations";

/**
 * @class
 * Responsável pela processamento da criação de uma respota para Financeiro
 */
export class FinancialMachine {
    constructor({ Serie } = {}) {
        this.Tools = new BrainTools();

        this.Serie = Serie;

        this.constants = {
            MES: true,
            QUANTIDADE: true
        };

        this.periods = {
            monthly: { count: 1 },
            quarterly: { count: 3, months: [3, 6, 9, 12] },
            semester: { count: 6, months: [6, 12] },
            yearly: { count: 12, months: [12] }
        };

        this.availableMonthsIndex = {
            monthly: [],
            quarterly: [
                [1, 2, 3],
                [4, 5, 6],
                [7, 8, 9],
                [10, 11, 12]
            ],
            semester: [
                [1, 2, 3, 4, 5, 6],
                [7, 8, 9, 10, 11, 12]
            ],
            yearly: [[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]]
        };

        this._setup();
    }

    accumulator(scenario, currentSerie, localPointsBySerie, isAccumulated) {
        const pointsReadOnly = _.cloneDeep(localPointsBySerie);
        if (
            ((scenario === "ORIGINAL" &&
                _.get(localPointsBySerie, "[0].cashflow", false)) ||
                scenario !== "ORIGINAL") &&
            !currentSerie.exp
        ) {
            localPointsBySerie.forEach((point) => {
                if (!isAccumulated) {
                    point.m.v = _.get(point, "m.v", _.get(point, "m.f", 0));
                } else {
                    let previousVal = _.reduce(
                        _.filter(pointsReadOnly, (p) => {
                            return (
                                p.t.getFullYear() === point.t.getFullYear() &&
                                p.t.getTime() < point.t.getTime()
                            );
                        }),
                        (acc, curr) => {
                            acc =
                                acc + _.get(curr, "m.v", _.get(curr, "m.f", 0));
                            return acc;
                        },
                        0
                    );

                    point.m.v =
                        _.get(point, "m.v", _.get(point, "m.f", 0)) +
                        previousVal;
                }
            });
        } else {
            if (!isAccumulated && !currentSerie.exp) {
                localPointsBySerie.forEach((point) => {
                    if (parseInt(DateUtils.dateToString(point.t, "M")) === 1)
                        point.m.v = _.get(point, "m.v", _.get(point, "m.f", 0));
                    else {
                        const previousPoint = _.find(pointsReadOnly, {
                            t: new Date(CalcOperation.helperMonths(point.t, -1))
                        });

                        const previousVal = _.get(previousPoint, "m.f", 0);

                        point.m.v =
                            _.get(point, "m.v", _.get(point, "m.f", 0)) -
                            previousVal;
                    }
                });
            } else {
                localPointsBySerie.forEach((point) => {
                    if (!point.m.v) {
                        point.m.v = _.get(point, "m.v", _.get(point, "m.f", 0));
                    }
                });
            }
        }

        return localPointsBySerie;
    }

    run(
        serie,
        points,
        scenario,
        period,
        timesAvailable,
        opts = {},
        _source,
        totalCompanies
    ) {
        const pointsSorted = _.sortBy(points, ["t"]);

        timesAvailable = !timesAvailable
            ? Object.keys(_.groupBy(pointsSorted, "t"))
            : timesAvailable;

        const pointsCalculated = [];

        const monthsArr = _.get(this.periods, `${period}.months`, []);

        const serieMinValue = _.get(serie, "metadata.minValue", null);

        const isParentAccumulated = _.get(serie, "metadata.accumulated", false);

        for (let i in timesAvailable) {
            let timeAvailable = timesAvailable[i];
            let time = 0;

            if (timeAvailable !== 0 && timeAvailable !== "0")
                time = DateUtils.getDate(timeAvailable);

            let year = time !== 0 ? time.getFullYear() : null;

            if (
                time !== 0 &&
                (period === "monthly" ||
                    monthsArr.includes(
                        parseInt(DateUtils.dateToString(time, "M"))
                    ))
            ) {
                const availableMonthListIdx = this.availableMonthsIndex[
                    period
                ].findIndex(
                    (idx) =>
                        idx &&
                        idx.includes(
                            parseInt(DateUtils.dateToString(time, "M"))
                        )
                );

                let exp = this.Tools.rulesRemove(serie.exp);

                const variables = this.Tools.expressionSplit(serie.exp);

                for (let j in variables) {
                    let currentPoint = {};
                    let pointsByYear = [];
                    let variable = variables[j];
                    const rule = this.Tools.ruleExtract(variable);

                    variable = this.Tools.serieIdExtract(variable);

                    const regex = this.Tools.serieExchangeReference(variable);

                    variable = this.Tools.specialCharacterRemove(variable);

                    for (let i in pointsSorted) {
                        let point = pointsSorted[i];
                        if (
                            (point.t === "0" ||
                                point.t === 0 ||
                                point.t.getTime() == time.getTime()) &&
                            variable === point.s
                        ) {
                            currentPoint = point;
                            break;
                        }
                    }

                    if (!currentPoint.isConstant) {
                        pointsByYear = _.filter(
                            pointsSorted,
                            (p) =>
                                year &&
                                p.s === currentPoint.s &&
                                p.t.getFullYear() === year
                        );
                    }

                    let value = this._getValueByCondition({
                        time,
                        availableIdx: availableMonthListIdx,
                        rule,
                        variable,
                        scenario,
                        period,
                        serie,
                        currentSerieId: currentPoint.s,
                        currentPoint: currentPoint.m,
                        points: pointsByYear,
                        isParentAccumulated,
                        opts,
                        totalCompanies
                    });

                    if (value) value = value.toFixed(2);
                    if (isNaN(value)) value = 0;

                    exp = exp.replace(regex, value);
                }

                exp = exp.replace(/\[|\]/g, "");
                const expValue = MathOperations(exp);

                let expResult = 0;

                if (serieMinValue) {
                    expResult =
                        expValue > serieMinValue ? expValue : serieMinValue;
                } else if (isFinite(expValue)) {
                    expResult = expValue;
                }

                pointsCalculated.push({
                    t: time,
                    s: serie._id,
                    c: serie.companyId,
                    sc: scenario,
                    u: DateUtils.getDate(new Date()),
                    m: { v: expResult },
                    period,
                    cashflow: opts.isCashflow
                });
            }
        }

        return pointsCalculated;
    }

    _setup() {
        const financialCalc = {
            ORIGINAL: this._originalScenarioPointsCalculate,
            custom: this._customScenarioPointsCalculate
        };

        const handler = {
            get: (target, name) => {
                return target.hasOwnProperty(name)
                    ? target[name]
                    : target["custom"];
            }
        };

        this.financialProxy = new Proxy(financialCalc, handler);

        const { original, scenario } = {
            original: {
                i: this._temporalRuleFunction,
                v: this._valueRuleFunction,
                custom: this._defaultRuleFunction
            },
            scenario: {
                i: this._scenarioCustomTemporalRuleFunction,
                v: this._scenarioCustomValueRuleFunction,
                custom: this._scenarioCustomDefaultRuleFunction
            }
        };

        this.originalRulesProxy = new Proxy(original, handler);

        this.scenarioRulesProxy = new Proxy(scenario, handler);
    }

    /**
     * @private
     * Realiza tratamento dos values de points de acordo com uma condição de calculo
     * @param {Object} point point que ira receber uma condição
     * @param {Array} points todos os points usados no kpi, para realizar tratamentos
     */
    _getValueByCondition({
        time,
        availableIdx,
        serie,
        currentPoint,
        currentSerieId,
        points,
        variable,
        scenario,
        period,
        rule,
        isParentAccumulated,
        opts,
        totalCompanies
    } = {}) {
        const monthPeriod = isParentAccumulated
            ? 1
            : this.periods[period].count;

        if (this.constants[variable])
            return CalcOperation.isConstant(variable, time, points);

        if (!currentPoint) return 0;

        if (
            this.periods[period].months &&
            !_.includes(
                this.periods[period].months,
                parseInt(DateUtils.dateToString(time, "M"))
            )
        ) {
            return 0;
        }

        const currentSerie =
            this.Serie.findOneCollection({
                _id: currentSerieId
            }) || {};

        const params =
            scenario === "ORIGINAL"
                ? {
                      time,
                      rule,
                      points,
                      availableIdx,
                      monthPeriod,
                      currentPoint,
                      currentSerieId,
                      isCashflow: opts.isCashflow,
                      period,
                      isParentAccumulated,
                      opts
                  }
                : {
                      period,
                      time,
                      availableIdx,
                      monthPeriod,
                      rule,
                      currentSerieId,
                      points,
                      currentPoint,
                      isParentAccumulated,
                      opts
                  };

        let val = this.financialProxy[scenario](params);

        if (
            !currentSerie.groupId &&
            opts.avg &&
            serie.groupId &&
            !serie.metadata.kpi &&
            currentSerie.unit != "percentage"
        ) {
            val = +(val / totalCompanies).toFixed(2);
        }

        return val;
    }

    _originalScenarioPointsCalculate = ({
        time,
        rule,
        availableIdx,
        points,
        monthPeriod,
        currentPoint,
        currentSerieId,
        isCashflow,
        period,
        isParentAccumulated,
        opts
    } = {}) => {
        const clonedPoints = _.cloneDeep(points);
        const serie = this.Serie.findOneCollection({ _id: currentSerieId });

        if (serie.type === "constant")
            return currentPoint.c || currentPoint.v || 0;

        const ruleKey = _.get(rule, "i", false)
            ? "i"
            : !_.isEmpty(rule)
            ? "v"
            : "custom";

        let previousValue = this._reducePrev(clonedPoints, time);

        if (period !== "monthly") {
            const currentPointIndex = clonedPoints.findIndex(
                (p) => p.t.getTime() === time.getTime()
            );
            const currentIndex =
                currentPointIndex === 0 ? 1 : currentPointIndex + 1;
            const pointRelationIndex =
                currentIndex - monthPeriod <= 0
                    ? 0
                    : currentIndex - monthPeriod;

            const pointsTimeFrame = clonedPoints.slice(
                pointRelationIndex,
                currentIndex
            );

            const sumPeriodAggregate = pointsTimeFrame.reduce((sum, point) => {
                previousValue = this._reducePrev(clonedPoints, point.t);

                let paramsMap = {
                    i: {
                        time: point.t,
                        rule,
                        points: clonedPoints,
                        currentPoint: point.m,
                        isCashflow,
                        isParentAccumulated,
                        opts
                    },
                    v: {
                        rule,
                        currentPoint: point.m,
                        isCashflow,
                        previousValue,
                        isParentAccumulated,
                        opts
                    },
                    custom: {
                        currentPoint: point.m,
                        isCashflow,
                        previousValue,
                        isParentAccumulated,
                        opts
                    }
                };

                if (
                    this.availableMonthsIndex[period][availableIdx].includes(
                        parseInt(DateUtils.dateToString(point.t, "M"))
                    )
                ) {
                    sum =
                        sum +
                        this.originalRulesProxy[ruleKey](paramsMap[ruleKey]);
                }

                return sum;
            }, 0);

            return sumPeriodAggregate;
        }

        let paramsMap = {
            i: {
                time,
                rule,
                points: clonedPoints,
                currentPoint,
                isCashflow,
                isParentAccumulated,
                opts
            },
            v: {
                rule,
                currentPoint,
                isCashflow,
                previousValue,
                isParentAccumulated,
                opts
            },
            custom: {
                currentPoint,
                isCashflow,
                previousValue,
                isParentAccumulated,
                opts
            }
        };

        return this.originalRulesProxy[ruleKey](paramsMap[ruleKey]);
    };

    _customScenarioPointsCalculate = ({
        time,
        rule,
        availableIdx,
        points,
        currentSerieId,
        monthPeriod,
        currentPoint,
        isCashflow,
        period,
        isParentAccumulated,
        opts
    } = {}) => {
        const clonedPoints = _.cloneDeep(points);

        const serie = this.Serie.findOneCollection({ _id: currentSerieId });

        if (serie.type === "constant")
            return currentPoint.c || currentPoint.v || 0;

        const ruleKey = _.get(rule, "i", false)
            ? "i"
            : !_.isEmpty(rule)
            ? "v"
            : "custom";

        let previousValue = this._reducePrev(clonedPoints, time, "v");

        let paramsMap = {
            i: {
                time,
                rule,
                points: clonedPoints,
                currentPoint,
                isCashflow,
                isParentAccumulated,
                opts
            },
            v: {
                rule,
                currentPoint,
                isCashflow,
                previousValue,
                isParentAccumulated,
                opts
            },
            custom: {
                currentPoint,
                previousValue,
                isParentAccumulated,
                opts
            }
        };

        if (period !== "monthly") {
            const currentPointIndex = clonedPoints.findIndex(
                (p) => +p.t === +time
            );
            const pointRelationIndex = currentPointIndex + monthPeriod;
            const pointsTimeFrame = points.slice(
                currentPointIndex,
                pointRelationIndex
            );

            const sumPeriodAggregate = pointsTimeFrame.reduce((sum, point) => {
                previousValue = this._reducePrev(clonedPoints, point.t);

                paramsMap = {
                    i: {
                        time: point.t,
                        rule,
                        points: clonedPoints,
                        currentPoint: point.m,
                        isCashflow,
                        isParentAccumulated,
                        opts
                    },
                    v: {
                        rule,
                        currentPoint: point.m,
                        isCashflow,
                        previousValue,
                        isParentAccumulated,
                        opts
                    },
                    custom: {
                        currentPoint: point.m,
                        previousValue,
                        isParentAccumulated,
                        opts
                    }
                };

                if (
                    this.availableMonthsIndex[period][availableIdx].includes(
                        parseInt(DateUtils.dateToString(point.t, "M"))
                    )
                ) {
                    sum =
                        sum +
                        this.scenarioRulesProxy[ruleKey](paramsMap[ruleKey]);
                }

                return sum;
            }, 0);

            return sumPeriodAggregate;
        } else return this.scenarioRulesProxy[ruleKey](paramsMap[ruleKey]);
    };

    _temporalRuleFunction = ({
        time,
        rule,
        points,
        currentPoint,
        isCashflow,
        isParentAccumulated,
        opts
    } = {}) => {
        if (parseInt(DateUtils.dateToString(time, "M")) === 1)
            return currentPoint.f || 0;

        let previousValue = this._reducePrev(points, time);

        const previousPoint = _.find(points, {
            t: new Date(CalcOperation.helperMonths(time, rule.i))
        });

        if (Math.abs(rule.i) > 1) {
            let sign = Math.sign(rule.i);
            currentPoint = _.find(points, {
                t: new Date(
                    CalcOperation.helperMonths(
                        time,
                        sign * (Math.abs(rule.i) - 1)
                    )
                )
            });

            if (!currentPoint) return 0;

            previousValue = this._reducePrev(points, currentPoint.t);

            currentPoint = currentPoint.m;
        }

        if (!isCashflow) {
            if (!previousPoint && previousValue) {
                return currentPoint["f"] - previousValue || 0;
            } else if (previousPoint) {
                return (
                    (currentPoint["f"] || 0) - (previousPoint["m"]["f"] || 0)
                );
            } else {
                return currentPoint["f"] || 0;
            }
        } else {
            if (opts.accumulatedView || isParentAccumulated) {
                if (!previousPoint && previousValue) {
                    return currentPoint["f"] + previousValue || 0;
                } else if (previousPoint) {
                    return (
                        (currentPoint["f"] || 0) +
                        (previousPoint["m"]["f"] || 0)
                    );
                }
            } else {
                return currentPoint["f"] || 0;
            }
        }
    };

    _valueRuleFunction = ({
        rule,
        currentPoint,
        isCashflow,
        previousValue,
        isParentAccumulated,
        opts
    } = {}) => {
        if ((opts.accumulatedView || isParentAccumulated) && isCashflow) {
            if (_.get(rule, "v")) {
                return currentPoint[rule.v] + previousValue;
            } else {
                return (
                    (_.isNumber(currentPoint.f) ? currentPoint.f : 0) +
                    previousValue
                );
            }
        } else {
            if (_.get(rule, "v")) {
                return currentPoint[rule.v];
            } else {
                return _.isNumber(currentPoint.f) ? currentPoint.f : 0;
            }
        }
    };

    _defaultRuleFunction = ({
        currentPoint,
        isCashflow,
        previousValue,
        isParentAccumulated,
        opts
    } = {}) => {
        if (isCashflow && (opts.accumulatedView || isParentAccumulated)) {
            return (
                (_.isNumber(currentPoint["v"])
                    ? currentPoint["v"]
                    : _.isNumber(currentPoint["f"])
                    ? currentPoint["f"]
                    : 0) + previousValue
            );
        }

        return _.isNumber(currentPoint["v"])
            ? currentPoint["v"]
            : _.isNumber(currentPoint["f"])
            ? currentPoint["f"]
            : 0;
    };

    _scenarioCustomTemporalRuleFunction = ({
        time,
        rule,
        points,
        currentPoint
    } = {}) => {
        if (parseInt(DateUtils.dateToString(time, "M")) === 1)
            return currentPoint["v"] || 0;

        let prevPoint = null;

        if (Math.abs(rule.i) > 1) {
            let sign = Math.sign(rule.i);
            prevPoint = _.find(points, {
                t: new Date(
                    CalcOperation.helperMonths(
                        time,
                        sign * (Math.abs(rule.i) - 1)
                    )
                )
            });
        }

        if (prevPoint) {
            return currentPoint["v"] - _.get(previousValue, "m.v", 0) || 0;
        } else {
            return currentPoint["v"] || 0;
        }
    };

    _scenarioCustomValueRuleFunction = ({
        rule,
        currentPoint,
        isCashflow,
        previousValue,
        isParentAccumulated,
        opts
    } = {}) => {
        if ((opts.accumulatedView || isParentAccumulated) && isCashflow) {
            if (_.get(rule, "v")) {
                return currentPoint[rule.v] + previousValue;
            } else {
                return (
                    (_.isNumber(currentPoint["v"]) ? currentPoint["v"] : 0) +
                    previousValue
                );
            }
        } else {
            if (_.get(rule, "v")) {
                return currentPoint[rule.v];
            } else {
                return _.isNumber(currentPoint["v"]) ? currentPoint["v"] : 0;
            }
        }
    };

    _scenarioCustomDefaultRuleFunction = ({
        currentPoint,
        previousValue,
        isParentAccumulated,
        opts
    } = {}) => {
        if (opts.accumulatedView || isParentAccumulated) {
            return (
                (_.isNumber(currentPoint["v"]) ? currentPoint["v"] : 0) +
                previousValue
            );
        }

        return _.isNumber(currentPoint["v"]) ? currentPoint["v"] : 0;
    };

    _reducePrev(points, time, key = "f") {
        return points.reduce((total, value) => {
            if (
                value.t < time &&
                DateUtils.dateToString(value.t, "Y") ===
                    DateUtils.dateToString(time, "Y")
            ) {
                total += value["m"][key] || 0;
            }
            return total;
        }, 0);
    }
}
